/**
 * The few lines that touch the real `document`, `localStorage` and
 * `matchMedia`.
 *
 * Everything interesting about resolving a motion preference is in
 * `src/motion.ts`, which takes all three as arguments and so can be tested
 * without a DOM. This is the other end of that: the one module in the pair
 * that names a global, kept to exactly the three it needs so that the tested
 * part stays the whole part. `src/theme-document.ts` is its twin.
 */

import { createMotionController, REDUCED_MOTION_QUERY, type MotionController } from './motion'
import type { MotionPreference } from './lib/motion'

/** `localStorage`, or nothing — Safari in private mode throws on the property. */
function safeStorage() {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

/** The device's own answer, or nothing in a browser too old to have one. */
function safeMedia() {
  try {
    return window.matchMedia?.(REDUCED_MOTION_QUERY) ?? null
  } catch {
    return null
  }
}

/**
 * One controller for the document, made on first use rather than at import,
 * so that importing this module from somewhere without a `window` — a test
 * reaching for the type, a server bundle that grew the wrong import — is not
 * itself the crash.
 */
let controller: MotionController | null = null

export function motion(): MotionController {
  controller ??= createMotionController({
    root: document.documentElement,
    storage: safeStorage(),
    media: safeMedia(),
  })
  return controller
}

/**
 * What the app does when it learns what this account asked for: draw it, and
 * remember the resolved answer for the next first paint. One call, because
 * doing one without the other is how the attribute on `<html>` and the copy
 * the inline script reads drift apart.
 */
export function showMotion(preference: MotionPreference): void {
  motion().set(preference)
}
